import { ValidBusinessObject } from '@iotv/iotv-v3-types'
import { ValidModelObject, ValidModelType } from '../../classDependentTypes/ValidModelClasses'
import { ITimestamp_TTL } from '../Interfaces/TimeRelated'
import { DeliveryType } from '../Notifications/enums'
import { AlertIncidentType, Severity } from './enums'
import { Incident } from './Incident'

export type IncidentIdParams = {
  incidentEntity: ValidBusinessObject
  incidentType: AlertIncidentType
  incidentSubtype?: string
  initiator: ValidBusinessObject
  timestamp: number
}

export const getIncidentId = ({ incidentEntity, incidentType, incidentSubtype, timestamp }: IncidentIdParams): string => {
  return `${incidentEntity.sk}:${incidentType}${incidentSubtype ? `:${incidentSubtype}` : ''}:${timestamp}`
}

export type IncidentDSKParams = {
  incidentEntity: ValidBusinessObject
  incidentType: AlertIncidentType
  incidentSubtype?: string
  initiator: ValidBusinessObject
}

/**
 * DSK is a colon-separated list of:
 * - Entity SK
 * - Incident type
 * - (Optional) Incident subtype
 * - Incident initiator SK
 * 
 * If there is no incident subtype, this results in:  
 * `entity type : ID : incident type : incident initiator type : ID`
 * 
 * If there is an incident subtype, this results in:  
 * `entity type : ID : incident type : ruleset type : ID : incident initiator type : ID`
 */
export const getIncidentDSK = ({ incidentEntity, incidentType, incidentSubtype, initiator }: IncidentDSKParams): string => {
  return `${incidentEntity.sk}:${incidentType}${incidentSubtype ? `:${incidentSubtype}` : ''}:${initiator.sk}`
}

export enum IncidentStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export enum IncidentEvent {
  OPENED = 'OPENED',
  UPDATED = 'UPDATED',
  CLOSED = 'CLOSED',
}

export type IncidentHistoryRecord = {
  initiator: ValidBusinessObject
  timestamp: number
  event: IncidentEvent | AlertIncidentType
  summary?: string
  alertSk?: string
}

export type NotificationRecord = {
  recipient: ValidModelObject<'User' | 'Customer'>
  timestampMs: number
  deliveryType: DeliveryType
  escalationFrom?: ValidModelObject<ValidModelType>
}

export type NotificationRecords = { [recipientSk: string]: NotificationRecord[] }

export type GeneratedNotificationContent = {
  title: string
  htmlContent: string
  textContent: string
}
export type NotificationGenerator = (incident: Incident) => Promise<GeneratedNotificationContent>

export interface IIncident extends ITimestamp_TTL {
  status?: IncidentStatus
  severity?: Severity
  summary?: string | undefined
  incidentEntity?: ValidBusinessObject
  incidentType?: AlertIncidentType
  incidentSubtype?: string
  initiator?: ValidBusinessObject
  closedTimestamp: number | undefined
  history?: IncidentHistoryRecord[]
  notificationRecords?: NotificationRecords
}
